import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { MultiIdCombobox } from '@/components/ui/multi-id-combobox';
import FormGeoLocationField from './formFields/FormGeoLocationField';
import FormSymbologySelect from 'components/MapPage/CorporateViewTabs/LayersTab/FormSymbologySelect';
import ICSChecklist from './ICSChecklist';
import type { ActionChecklistItem } from './actionChecklistModel';
import {
  ACTION_PRIORITY_OPTIONS,
  visibleActionStatusOptions,
  ActionAssigneeKind,
} from './ICSConstants';
import { useHwcgFlag } from 'utils/featureFlags';
import { useAorText } from 'utils/aorTerminology';
import {
  ACTION_CLASSIFICATION_HINT,
  ACTION_CLASSIFICATION_OPTIONS,
} from './actionClassification';
import type { ActionEntity } from './hooks/useScopedActionForm';
import { ResponsibleIndividualsField } from './actionTicket/ResponsibleIndividualsField';
import { TicketCheckboxField } from './actionTicket/TicketCheckboxField';
import { TicketDateTimeField } from './actionTicket/TicketDateTimeField';
import { TicketSelectField } from './actionTicket/TicketSelectField';
import { TicketTextAreaField } from './actionTicket/TicketTextAreaField';
import { TicketTextField } from './actionTicket/TicketTextField';

export type ActionFormFieldsProps = {
  isEdit: boolean;
  selectedEntity?: ActionEntity;
  displayId?: string | null;
  workspaceLabel?: string;
  assigneeKind: ActionAssigneeKind;
  assigneeKindOptions: { label: string; value: string }[];
  aorOptions: { label: string; value: string }[];
  objectiveOptions?: { label: string; value: string }[];
  /** Incident scope only; org actions are not tied to ICS-202 objectives. */
  showObjective?: boolean;
  pocOptions: { label: string; value: string }[];
  teamOptions: { label: string; value: string }[];
  positionOptions: { label: string; value: string }[];
  roleOptions?: { label: string; value: string }[];
  /** Replaces the single AOR select with a Responsible AOR multiselect. */
  aorTitle?: string;
  aorMulti?: boolean;
  dependencyChoices: { label: string; value: string }[];
  dependsOn: string[];
  onDependsOnChange: (next: string[]) => void;
  responsibleIndividualOptions?: { label: string; value: string }[];
  /** People a sub-task can name. Independent of the task-level selection. */
  subTaskResponsibleOptions?: { label: string; value: string }[];
  responsibleIndividualIds?: string[];
  onResponsibleIndividualIdsChange?: (next: string[]) => void;
  assigneeId?: string | null;
  /** Incident scope only; org actions reach neither the ICS-233 nor the ICS-215. */
  showClassification: boolean;
  checklist: ActionChecklistItem[];
  setChecklist: React.Dispatch<React.SetStateAction<ActionChecklistItem[]>>;
  /** Scopes the checklist file picker; absent for an org action. */
  incidentId?: string | null;
  /** Files derived from position-tagged folders; empty for most tasks. */
  positionFiles?: React.ReactNode;
};

/**
 * The stacked action form as it was before the ticket layout. Still the body of
 * `ActionDrawer` whenever `ACTION_TICKETS` is off.
 */
const ActionFormFields: React.FC<ActionFormFieldsProps> = ({
  isEdit,
  displayId,
  workspaceLabel,
  assigneeKind,
  assigneeKindOptions,
  aorOptions,
  objectiveOptions = [],
  showObjective = false,
  pocOptions,
  teamOptions,
  positionOptions,
  roleOptions = [],
  aorTitle,
  aorMulti = false,
  dependencyChoices,
  dependsOn,
  onDependsOnChange,
  responsibleIndividualOptions = [],
  subTaskResponsibleOptions = [],
  responsibleIndividualIds = [],
  onResponsibleIndividualIdsChange,
  assigneeId,
  showClassification,
  checklist,
  setChecklist,
  incidentId,
  positionFiles,
}) => {
  const statusOptions = visibleActionStatusOptions(useHwcgFlag());
  const aorText = useAorText();
  return (
    <div className="tw:space-y-4">
      <div className="tw:grid tw:grid-cols-1 tw:gap-3 tw:sm:grid-cols-3">
        <div className="tw:space-y-1.5">
          <div className="tw:text-xs tw:font-semibold tw:tracking-wide tw:uppercase tw:text-muted-foreground">
            ID
          </div>
          <div className="tw:font-mono tw:text-sm tw:text-muted-foreground">
            {displayId || 'Assigned on save'}
          </div>
        </div>
        {aorMulti ? (
          <ResponsibleAorField
            title={aorText(aorTitle ?? 'Responsible AOR')}
            options={aorOptions}
          />
        ) : (
          <TicketSelectField
            title={aorText(aorTitle ?? 'AOR')}
            fieldName="aor_id"
            options={aorOptions}
            isClearable
          />
        )}
        {showObjective ? (
          <TicketSelectField
            title="Objective"
            fieldName="parent_id"
            options={objectiveOptions}
            isClearable
            placeholder="In service of"
          />
        ) : null}
        <div className="tw:space-y-1.5">
          <div className="tw:text-xs tw:font-semibold tw:tracking-wide tw:uppercase tw:text-muted-foreground">
            Workspace
          </div>
          <div className="tw:text-sm tw:text-muted-foreground">
            {workspaceLabel || '—'}
          </div>
        </div>
      </div>
      <TicketTextField
        title="Task"
        fieldName="name"
        rules={{
          required: { value: true, message: 'This field is required' },
          maxLength: {
            value: 250,
            message: 'This maximum length is 250 characters',
          },
        }}
      />
      <TicketTextAreaField
        title="Description"
        fieldName="description"
        placeholder="Optional detail: what done looks like, constraints, references."
        rules={{
          maxLength: {
            value: 2000,
            message: 'This maximum length is 2000 characters',
          },
        }}
      />
      <div className="tw:grid tw:grid-cols-1 tw:gap-3 tw:sm:grid-cols-2">
        {assigneeKindOptions.length > 1 && (
          <TicketSelectField
            title="Assign To"
            fieldName="assignee_kind"
            options={assigneeKindOptions}
          />
        )}
        {assigneeKind === 'user' && (
          <TicketSelectField
            title="Assignee"
            fieldName="poc"
            options={pocOptions}
            isClearable
          />
        )}
        {assigneeKind === 'team' && (
          <TicketSelectField
            title="Team"
            fieldName="assignee_id"
            options={teamOptions}
            isClearable
          />
        )}
        {assigneeKind === 'position' && (
          <TicketSelectField
            title="ICS Position"
            fieldName="assignee_id"
            options={positionOptions}
            isClearable
          />
        )}
        {assigneeKind === 'role' && (
          <TicketSelectField
            title="Role"
            fieldName="assignee_id"
            options={roleOptions}
            isClearable
          />
        )}
      </div>
      {assigneeKind === 'position' && onResponsibleIndividualIdsChange ? (
        <ResponsibleIndividualsField
          positionSelected={Boolean(assigneeId)}
          options={responsibleIndividualOptions}
          value={responsibleIndividualIds}
          onChange={onResponsibleIndividualIdsChange}
        />
      ) : null}
      <TicketCheckboxField title="Briefed Assignee" fieldName="briefed_poc" />
      <div className="tw:grid tw:grid-cols-1 tw:gap-3 tw:sm:grid-cols-2">
        <TicketSelectField
          title="Status"
          fieldName="status"
          options={statusOptions}
          rules={{
            required: { value: true, message: 'Status is required' },
          }}
        />
        <TicketSelectField
          title="Priority"
          fieldName="priority"
          options={ACTION_PRIORITY_OPTIONS}
        />
      </div>
      {showClassification ? (
        <div className="tw:grid tw:grid-cols-1 tw:gap-3 tw:sm:grid-cols-2">
          <TicketSelectField
            title="Classification"
            fieldName="classification"
            options={ACTION_CLASSIFICATION_OPTIONS}
          />
          <p className="tw:self-end tw:text-[0.675rem] tw:text-muted-foreground">
            {ACTION_CLASSIFICATION_HINT}
          </p>
        </div>
      ) : null}
      <TicketDateTimeField title="Start Date & Time" fieldName="start_date" />
      <TicketDateTimeField title="Target Date & Time" fieldName="target_date" />
      {dependencyChoices.length > 0 && (
        <div className="tw:space-y-1.5">
          <div className="tw:text-xs tw:font-semibold tw:tracking-wide tw:uppercase tw:text-muted-foreground">
            Blocked By
          </div>
          <MultiIdCombobox
            options={dependencyChoices}
            value={dependsOn}
            onChange={onDependsOnChange}
            placeholder="Tasks that must close first"
          />
          <p className="tw:m-0 tw:text-[0.675rem] tw:text-muted-foreground">
            This task stays blocked until every task listed here is Closed.
            Tasks already waiting on this one are not offered.
          </p>
        </div>
      )}
      <div className="tw:space-y-1.5">
        <FormSymbologySelect variant="shadcn" />
        <p className="tw:m-0 tw:text-[0.675rem] tw:text-muted-foreground">
          The symbol this task draws with on the map. Leave as None to keep the
          status and priority marker.
        </p>
      </div>
      <div className="tw:space-y-1.5">
        <div className="tw:text-xs tw:font-semibold tw:tracking-wide tw:uppercase tw:text-muted-foreground">
          Location
        </div>
        <FormGeoLocationField name="geolocation" label="" />
      </div>
      <ICSChecklist
        checklist={checklist}
        setChecklist={setChecklist}
        editable
        existingData={isEdit}
        incidentId={incidentId}
        asSection
        responsibleIndividualOptions={subTaskResponsibleOptions}
      />
      {positionFiles}
    </div>
  );
};

export default ActionFormFields;

function ResponsibleAorField({
  title,
  options,
}: {
  title: string;
  options: { label: string; value: string }[];
}) {
  const { control } = useFormContext();
  return (
    <Controller
      control={control}
      name="aor_ids"
      render={({ field }) => (
        <div className="tw:space-y-1.5">
          <div className="tw:text-xs tw:font-semibold tw:tracking-wide tw:uppercase tw:text-muted-foreground">
            {title}
          </div>
          <MultiIdCombobox
            options={options}
            value={field.value ?? []}
            onChange={field.onChange}
            placeholder="Search AORs…"
          />
        </div>
      )}
    />
  );
}
